import { useState } from 'react';

import { Card, Button, Accordion, Badge, Form, Alert, Spinner, Modal } from 'react-bootstrap';

const CATEGORY_VARIANTS = {
    'Authentication': 'primary',
    'Cleanup': 'dark',
    'Data Entry': 'warning',
    'Navigation': 'info',
    'Setup': 'secondary',
    'Validation': 'success'
};

export const StepReviewPhases = ({ autoApprove, error, freeform, onBack, onRegenerate, onStart, phases, regenerating, setAutoApprove, testPlan }) => {

    const [activeKeys, setActiveKeys] = useState([]);
    const [showRegenModal, setShowRegenModal] = useState(false);
    const [regenNotes, setRegenNotes] = useState('');

    const closeRegen = () => {

        setShowRegenModal(false); setRegenNotes('');

    };
    const submitRegen = () => {

        const notes = regenNotes.trim() || null;
        closeRegen();
        onRegenerate(notes);

    };

    if (freeform) {

        return (

            <Card>
                <Card.Body>

                    <Card.Title>
                        Review brief
                    </Card.Title>
                    <Card.Subtitle className="text-muted mb-3">
                        Freeform mode — the AI will read this goal, look at the live page, and decide each step
                        as it goes. No upfront plan, no phase tracking.
                    </Card.Subtitle>

                    {error && (
                    <Alert variant="danger">
                        {error}
                    </Alert>
                    )}

                    <div
                        className="mb-3 p-3 rounded border"
                        style={{ background: 'rgba(0,0,0,0.02)', whiteSpace: 'pre-wrap' }}
                    >
                        {testPlan || (
                        <em className="text-muted">
                            No plan provided.
                        </em>
                        )}
                    </div>

                    <Form.Check
                        checked={autoApprove}
                        className="mb-3"
                        id="auto-approve"
                        label="Auto-approve each step (1.5s delay between actions)"
                        onChange={e => setAutoApprove(e.target.checked)}
                        type="checkbox"
                    />

                    <div className="d-flex justify-content-between align-items-center">
                        <Button onClick={onBack} variant="outline-secondary">
                            Back
                        </Button>
                        <Button onClick={onStart} variant="success">
                            Start testing
                        </Button>
                    </div>

                </Card.Body>
            </Card>

        );

    }

    if (!phases || phases.length === 0)
        return (
            <Card>
                <Card.Body>
                    <Card.Title>
                        Review phases
                    </Card.Title>
                    <Alert variant="warning">
                        No phases were generated. Go back and try a different test plan.
                    </Alert>
                    <Button onClick={onBack} variant="outline-secondary">
                        Back
                    </Button>
                </Card.Body>
            </Card>
        );

    const totalSteps = phases.reduce((sum, p) => sum + (p.steps?.length || 0), 0);

    return (

        <Card>
            <Card.Body>

                <Card.Title>
                    Review phases
                </Card.Title>
                <Card.Subtitle className="text-muted mb-3">
                    {phases.length}
                    {' '}
                    phase
                    {phases.length === 1 ? '' : 's'}
                    ,
                    {' '}
                    {totalSteps}
                    {' '}
                    step
                    {totalSteps === 1 ? '' : 's'}
                    {' '}
                    total. Expand any phase to see the actual steps.
                </Card.Subtitle>

                {error && (
                <Alert variant="danger">
                    {error}
                </Alert>
                )}

                <Accordion alwaysOpen activeKey={activeKeys} className="mb-3" onSelect={keys => setActiveKeys(keys || [])}>

                    {phases.map((phase, i) => (

                        <Accordion.Item eventKey={String(i)} key={i}>

                            <Accordion.Header>

                                <div className="d-flex align-items-center gap-2 flex-grow-1 pe-3">

                                    <Badge bg={CATEGORY_VARIANTS[phase.category] || 'secondary'}>
                                        {phase.category}
                                    </Badge>

                                    <span className="fw-semibold">
                                        {phase.label}
                                    </span>

                                    <small className="text-muted ms-auto">
                                        {phase.steps?.length || 0}
                                        {' '}
                                        step
                                        {phase.steps?.length === 1 ? '' : 's'}
                                    </small>

                                </div>

                            </Accordion.Header>

                            <Accordion.Body>

                                <ol className="mb-0 small">
                                    {(phase.steps || []).map((s, j) => (
                                        <li key={j}>
                                            {s}
                                        </li>
                                    ))}
                                </ol>

                            </Accordion.Body>

                        </Accordion.Item>

                    ))}

                </Accordion>

                <Form.Check
                    checked={autoApprove}
                    className="mb-3"
                    id="auto-approve"
                    label="Auto-approve each step (1.5s delay between actions)"
                    onChange={e => setAutoApprove(e.target.checked)}
                    type="checkbox"
                />

                <div className="d-flex justify-content-between align-items-center">

                    <Button disabled={regenerating} onClick={onBack} variant="outline-secondary">
                        Back
                    </Button>

                    <div className="d-flex gap-2">
                        <Button disabled={regenerating} onClick={() => setShowRegenModal(true)} variant="outline-primary">
                            {regenerating
                                ? (
                                    <>
                                        <Spinner animation="border" as="span" className="me-2" size="sm" />
                                        Regenerating…
                                    </>
)
                                : 'Regenerate'}
                        </Button>
                        <Button disabled={regenerating} onClick={onStart} variant="success">
                            Start testing
                        </Button>
                    </div>

                </div>

            </Card.Body>

            <Modal centered onHide={closeRegen} show={showRegenModal}>

                <Modal.Header closeButton>
                    <Modal.Title>
                        Regenerate phases
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>

                    <p className="text-muted small mb-2">
                        Optional. Tell the AI what should be different — it will use this as guidance when reorganizing.
                    </p>

                    <Form.Control
                        autoFocus
                        as="textarea"
                        onChange={e => setRegenNotes(e.target.value)}
                        placeholder={'e.g. "Group all login steps into one phase"\n\'Add a step to verify the URL bar after navigation\'\n"The validation phase missed the success toast"'}
                        rows={4}
                        value={regenNotes}
                    />

                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={closeRegen} variant="outline-secondary">
                        Cancel
                    </Button>
                    <Button onClick={submitRegen} variant="primary">
                        Regenerate
                    </Button>
                </Modal.Footer>

            </Modal>

        </Card>

    );

};
